import { useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import constants from "../config/constants";

/**
 * Custom hook to construct URLs and handle redirects to the external Web App
 * with or without accessToken.
 */
export const useRedirectTo = () => {
  const { user, accessToken: authAccessToken } = useAuth();

  /**
   * Generates the URL for the external Web App.
   * If an accessToken is present, it includes it as a query param.
   * If a redirectTo path is provided (e.g. "/subscription"), it appends redirectTo.
   *
   * @param {string} [redirectTo=""] Optional target path on the Web App (e.g. "/subscription")
   * @returns {string} The constructed URL
   */
  const createRedirectUrl = useCallback(
    (redirectTo = "") => {
      const token =
        authAccessToken ||
        user?.accessToken ||
        localStorage.getItem("accessToken");
      const baseUrl = constants?.WebUrl || "https://app.huashenherbs.com/";
      const cleanBase = baseUrl.replace(/\/+$/, "");

      if (redirectTo) {
        return token
          ? `${cleanBase}/login?accessToken=${encodeURIComponent(token)}&redirectTo=${redirectTo}`
          : `${cleanBase}/login?redirectTo=${redirectTo}`;
      }

      return token
        ? `${cleanBase}/login?accessToken=${encodeURIComponent(token)}`
        : `${cleanBase}/login`;
    },
    [authAccessToken, user?.accessToken]
  );

  /**
   * Redirects the browser window to the generated Web App URL.
   *
   * @param {string} [targetPath=""] Optional target path on the Web App
   */
  const redirectTo = useCallback(
    (targetPath = "") => {
      const url = createRedirectUrl(targetPath);
      window.location.href = url;
    },
    [createRedirectUrl]
  );

  /**
   * Helper specifically for managing subscription (redirectTo=/subscription).
   */
  const handleManageSubscription = useCallback(() => {
    redirectTo("/subscription");
  }, [redirectTo]);

  return {
    createRedirectUrl,
    redirectTo,
    handleManageSubscription,
  };
};

export default useRedirectTo;
